/**
 * Labstep
 *
 * @module components/ProtocolStep/Card/Detail
 * @desc Name and description of protocol step
 */

import { ProtocolStepActionComplete } from 'labstep-web/components/ProtocolStep/Action/Complete';
import { ThreadCountIcon } from 'labstep-web/components/Thread/CountIcon';
import { ICONS } from 'labstep-web/constants/icons';
import ProtocolChildRouter from 'labstep-web/containers/Router/ProtocolChild';
import { ActionComponentModalContainer as ActionComponent } from 'labstep-web/core/Action/Component';
import { Card } from 'labstep-web/core/Card/Card';
import { Icon } from 'labstep-web/core/Icon';
import { PopupContainer as Popup } from 'labstep-web/core/Popup';
import { SubHeader } from 'labstep-web/core/SubHeader';
import { Experiment } from 'labstep-web/models/experiment.model';
import { Protocol } from 'labstep-web/models/protocol.model';
import { ProtocolStep } from 'labstep-web/models/protocol-step.model';
import { Read } from 'labstep-web/prosemirror/components/NodeView/Read';
import type React from 'react';
import styles from './styles.module.scss';
import type { IProtocolStepCardDetailProps } from './types';

export const ProtocolStepCardDetail: React.FC<
  IProtocolStepCardDetailProps
> = ({
  protocol,
  protocolStepId,
  stepNumber,
  onDelete,
  timestamp,
}) => (
  <Card
    id={`protocol-step-${protocolStepId}`}
    className={styles.card}
  >
    {!!onDelete && (
      <div className={styles.unwrapStep}>
        <ActionComponent
          type="icon"
          icon="times"
          onClick={() => onDelete(protocolStepId)}
          elementProps={{
            popup: { content: 'Unwrap Step' },
          }}
        />
      </div>
    )}
    {protocol instanceof Protocol && (
      <div contentEditable={false} className={styles.completeStep}>
        <Popup
          trigger={
            <Icon
              size="big"
              color="grey"
              name={ICONS.protocol.step}
            />
          }
          content="You can mark steps as complete each time you run the protocol."
          inverted
        />
      </div>
    )}
    <div
      contentEditable={false}
      data-cy="components/ProtocolStep/Card/Detail:step"
    >
      <SubHeader>{`STEP ${stepNumber}`}</SubHeader>
    </div>

    {protocol instanceof Experiment && (
      <Read
        entityName={ProtocolStep.entityName}
        id={protocolStepId}
        timestamp={timestamp}
        placeholder={<div />}
      >
        {({ entity }) => (
          <>
            {!protocol.isTemplate && entity && (
              <>
                <div
                  contentEditable={false}
                  className={styles.completeStep}
                >
                  <ProtocolStepActionComplete
                    protocol={protocol}
                    protocolStep={entity as ProtocolStep}
                  />
                </div>

                <div className={styles.actions}>
                  <ProtocolChildRouter
                    route="step_show_thread"
                    id={entity.guid}
                  >
                    {({ navigate }) => (
                      <div
                        className="protocol-step-thread"
                        onClick={navigate}
                        contentEditable={false}
                      >
                        <ThreadCountIcon
                          count={
                            (entity as ProtocolStep).thread
                              .comment_count
                          }
                        />
                      </div>
                    )}
                  </ProtocolChildRouter>
                </div>
              </>
            )}
          </>
        )}
      </Read>
    )}
  </Card>
);
